import { useMemo } from 'react';
import { InternalUiRenderer } from '@erp/json-renderer/react';
import type { InternalUiSpec } from '@erp/json-renderer/react';
import { cn } from '@/lib/utils';

interface InternalUiToolResultProps {
  /** The `render_custom_view` tool-call args: `{ spec, state?, title? }`. */
  args: Record<string, unknown>;
  className?: string;
}

/**
 * Renders a full custom view Nami built via the `render_custom_view` tool,
 * inline in the chat. The spec is a json-render spec rendered with the @erp/ui
 * design system (and @erp/viz charts) — the same renderer used by the Custom UI
 * editor. Mirrors `ChartToolResult`: the frontend renders from the tool-call
 * args, so it works in every chat surface.
 */
export function InternalUiToolResult({
  args,
  className,
}: InternalUiToolResultProps) {
  const title = typeof args.title === 'string' ? args.title : undefined;

  const spec = useMemo<InternalUiSpec | null>(() => {
    const candidate = args.spec;
    return candidate &&
      typeof candidate === 'object' &&
      !Array.isArray(candidate)
      ? (candidate as InternalUiSpec)
      : null;
  }, [args.spec]);

  const state =
    args.state && typeof args.state === 'object' && !Array.isArray(args.state)
      ? (args.state as Record<string, unknown>)
      : {};

  if (!spec) return null;

  return (
    <div
      className={cn('rounded-lg border border-border bg-card p-3', className)}
    >
      {title && (
        <div className="mb-2 text-sm font-medium text-card-foreground">
          {title}
        </div>
      )}
      <InternalUiRenderer spec={spec} state={state} />
    </div>
  );
}
